'use client';
import React, { Suspense } from 'react';
import { IRepository } from '@/types';
import { IDataFrameResponse } from '@/lib/dataFrameTypes';
import { ICompareResponse, ITabularCompare } from '@/lib/tabularCompareApi';
import ErrorBoundary from '@/components/ErrorBoundary';
import ErrorMessage from '@/components/ErrorMessage';

import { TViewMode } from '../types';
import useDataframeContext, { DataframeContextProvider } from '../DataframeContext';
import { DataframeLayoutContextProvider } from '../DataFrameLayoutContext';
import useDataframeQueryContext from '../DataframeQueryContext';

import DataGrid from '../DataGrid';
import * as Styled from './styled';

import ControlHeaderRow from '../ControlHeaderRow';
import { DataframeLayoutSettings } from '../DataFrameLayoutContext';

interface IClientWrapperBaseProps {
  repository: IRepository;
  dataFrameResponse: IDataFrameResponse;
  mode: TViewMode;
  workspaceId?: string;
  searchParams?: any;
  layoutSettings?: DataframeLayoutSettings;
}

interface IClientWrapperFileProps extends IClientWrapperBaseProps {
  fileRootPath: string;
  branch: string;
  path: string;
  compareResponse?: never;
  evaluationTargetField?: never;
}

interface IClientWrapperCompareProps extends IClientWrapperBaseProps {
  fileRootPath?: never;
  branch?: never;
  path?: never;
  compareResponse: ICompareResponse | ITabularCompare;
  evaluationTargetField?: never;
}

interface IClientWrapperStaticProps extends IClientWrapperBaseProps {
  fileRootPath?: string;
  branch?: never;
  path?: never;
  compareResponse?: never;
  searchParams?: never;
  mode: 'view' | 'sample';
  evaluationTargetField?: string;
}

const ServerErrorMessage: React.FC<{ repository: IRepository; searchParams: any }> = ({ repository, searchParams }) => {
  const isQuery = searchParams.nlp || searchParams.sql;
  const errorTitle = isQuery
    ? 'The requested query could not be processed.'
    : 'The requested Dataframe could not be loaded.';
  return (
    <ErrorMessage title={errorTitle}>
      {isQuery && <div>We&apos;re constantly working to improve the query system.</div>}
      <div>The Oxen team has been notified, and are working on a solution.</div>
      {/* {isQuery && (
        <a className="link" href={`/${repository?.namespace}/${repository?.name}/file/${params.path.join('/')}`}>
          Return to Dataframe view
        </a>
      )} */}
    </ErrorMessage>
  );
};

const DataframeStatusWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { revalidating } = useDataframeQueryContext();
  const { isValidating, isLoading, editable, displayMode } = useDataframeContext();
  const isUpdating = revalidating || isValidating || isLoading;
  return (
    <Styled.DataFrameTable>
      {isUpdating && (
        <Styled.DataFrameTableLoadingOverlay>
          <span>Revalidating...</span>
        </Styled.DataFrameTableLoadingOverlay>
      )}
      {children}
    </Styled.DataFrameTable>
  );
};

const EditableStatus: React.FC = () => {
  const { revalidating } = useDataframeQueryContext();
  const { editable, displayMode, isLoading, isValidating } = useDataframeContext();
  const isUpdating = revalidating || isValidating || isLoading;
  return displayMode === 'edit' && editable === false ? (
    <Styled.EditableStatus>
      <div>Editing is diabled by this query, as the schema is incompatible with the source data.</div>
    </Styled.EditableStatus>
  ) : null;
};

const ClientWrapper: React.FC<IClientWrapperFileProps | IClientWrapperCompareProps | IClientWrapperStaticProps> = ({
  repository,
  branch,
  path,
  mode,
  workspaceId,
  fileRootPath,
  dataFrameResponse,
  searchParams,
  compareResponse,
  layoutSettings,
  evaluationTargetField,
}) => {
  return (
    <DataframeLayoutContextProvider settings={layoutSettings}>
      <DataframeContextProvider
        repository={repository}
        branch={branch}
        path={path}
        fileRootPath={fileRootPath}
        searchParams={searchParams}
        workspaceId={workspaceId}
        mode={mode}
        dataFrameResponse={dataFrameResponse}
        compareResponse={compareResponse}
      >
        <EditableStatus />
        <Styled.ClientWrapper className="dataframe-client-wrapper">
          <ErrorBoundary
            fallback={<ServerErrorMessage repository={repository as IRepository} searchParams={searchParams} />}
            containerComponent="DataframeLoader > ClientWrapper"
          >
            <ControlHeaderRow />
            <DataframeStatusWrapper>
              <Suspense fallback={<div>Loading Data...</div>}>
                <DataGrid evaluationTargetField={evaluationTargetField} />
              </Suspense>
            </DataframeStatusWrapper>
          </ErrorBoundary>
        </Styled.ClientWrapper>
      </DataframeContextProvider>
    </DataframeLayoutContextProvider>
  );
};

export default ClientWrapper;
